import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Send, ShieldCheck, CalendarCheck, Info } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

// A single row in the bulk PTO adder.
function blankRow() {
  return {
    key: Math.random().toString(36).slice(2),
    employee_id: "",
    requested_at: format(new Date(), "yyyy-MM-dd"),
    start_date: "",
    end_date: "",
    reason: "",
  };
}

export default function BulkPtoTab({ onSubmitted }) {
  const { facilityId } = useFacility();
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!facilityId) return;
    api.get(`/employees?facility_id=${facilityId}`).then(({ data }) => setEmployees(data || []))
       .catch(() => setEmployees([]));
  }, [facilityId]);

  const valid = useMemo(() =>
    rows.filter(r => r.employee_id && r.start_date && r.end_date),
    [rows]
  );

  const add = () => setRows(rs => [...rs, blankRow()]);
  const remove = (key) => setRows(rs => (rs.length > 1 ? rs.filter(r => r.key !== key) : rs));
  const update = (key, patch) => setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));

  const submit = async () => {
    if (valid.length === 0) { toast.error("Fill in at least one full row (employee + start + end)"); return; }
    setBusy(true);
    try {
      const entries = valid.map(r => ({
        employee_id: r.employee_id,
        start_date: r.start_date,
        end_date: r.end_date,
        reason: r.reason || null,
        requested_at: r.requested_at ? new Date(r.requested_at + "T09:00:00").toISOString() : null,
      }));
      const { data } = await api.post("/pto-requests/bulk", { entries });
      const good = (data.created || []).length;
      const bad = (data.failed || []).length;
      if (good) toast.success(`Filed ${good} PTO request${good === 1 ? "" : "s"}`);
      if (bad) {
        toast.error(`${bad} row${bad === 1 ? "" : "s"} failed`, {
          description: data.failed.slice(0, 3).map(f => `• ${f.error}`).join("\n"),
        });
        // Keep the failed rows visible for the admin to fix
        const failedKeys = new Set(
          data.failed.map(f => `${f.employee_id}|${f.start}|${f.end}`)
        );
        setRows(rs => rs.filter(r => failedKeys.has(`${r.employee_id}|${r.start_date}|${r.end_date}`))
                         .concat(blankRow()));
      } else {
        setRows([blankRow(), blankRow(), blankRow()]);
      }
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="bulk-pto-tab">
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 flex gap-3 items-start">
        <div className="w-8 h-8 rounded-lg bg-red-500 text-white grid place-items-center shrink-0">
          <ShieldCheck size={16} />
        </div>
        <div className="text-sm text-red-900">
          <div className="font-bold uppercase tracking-widest text-[11px]">Admin bulk-add</div>
          <div className="mt-0.5">
            File PTO requests on behalf of employees. The <b>Requested-on</b> date sets the submission
            timestamp so the days-notice math on the manager side stays accurate when catching up on
            paper submissions. Each row becomes a normal pending PTO request in the employee's
            requests tab.
          </div>
        </div>
      </div>

      <div className="hidden md:grid grid-cols-[minmax(180px,1.7fr)_130px_130px_130px_minmax(160px,1.6fr)_40px] gap-2 text-[10px] uppercase tracking-widest font-black text-slate-500 px-1">
        <div>Employee</div>
        <div>Requested on</div>
        <div>Start date</div>
        <div>End date</div>
        <div>Comment / reason</div>
        <div></div>
      </div>

      <div className="space-y-2" data-testid="bulk-pto-rows">
        {rows.map((r) => (
          <div key={r.key}
               className="grid grid-cols-1 md:grid-cols-[minmax(180px,1.7fr)_130px_130px_130px_minmax(160px,1.6fr)_40px] gap-2 items-start p-2 rounded-lg border border-slate-200 bg-white"
               data-testid={`bulk-pto-row-${r.key}`}>
            <div>
              <Label className="md:hidden text-[10px] text-slate-500 mb-1 block">Employee</Label>
              <Select value={r.employee_id || undefined} onValueChange={(v) => update(r.key, { employee_id: v })}>
                <SelectTrigger data-testid={`bulk-pto-emp-${r.key}`} className="h-9">
                  <SelectValue placeholder="Pick employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="md:hidden text-[10px] text-slate-500 mb-1 block">Requested on</Label>
              <Input type="date" value={r.requested_at} onChange={(e) => update(r.key, { requested_at: e.target.value })}
                     className="h-9" data-testid={`bulk-pto-reqon-${r.key}`} />
            </div>
            <div>
              <Label className="md:hidden text-[10px] text-slate-500 mb-1 block">Start date</Label>
              <Input type="date" value={r.start_date} onChange={(e) => update(r.key, { start_date: e.target.value })}
                     className="h-9" data-testid={`bulk-pto-start-${r.key}`} />
            </div>
            <div>
              <Label className="md:hidden text-[10px] text-slate-500 mb-1 block">End date</Label>
              <Input type="date" value={r.end_date} onChange={(e) => update(r.key, { end_date: e.target.value })}
                     className="h-9" data-testid={`bulk-pto-end-${r.key}`} />
            </div>
            <div>
              <Label className="md:hidden text-[10px] text-slate-500 mb-1 block">Comment</Label>
              <Textarea rows={1} value={r.reason} onChange={(e) => update(r.key, { reason: e.target.value })}
                        className="h-9 min-h-9 py-1.5" placeholder="Vacation, family, jury duty…"
                        data-testid={`bulk-pto-reason-${r.key}`} />
            </div>
            <button type="button" onClick={() => remove(r.key)}
                    className="mt-1 md:mt-2 h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove row" data-testid={`bulk-pto-remove-${r.key}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Button variant="outline" onClick={add} data-testid="bulk-pto-add-row">
          <Plus size={14} className="mr-1" /> Add row
        </Button>
        <div className="flex-1 text-xs text-slate-500 flex items-center gap-1">
          <Info size={11} /> {valid.length} of {rows.length} row{rows.length === 1 ? "" : "s"} ready to submit
        </div>
        <Button className="bg-red-600 hover:bg-red-700" disabled={busy || valid.length === 0}
                onClick={submit} data-testid="bulk-pto-submit">
          <Send size={14} className="mr-1" /> {busy ? "Filing…" : `File ${valid.length || ""} PTO request${valid.length === 1 ? "" : "s"}`.trim()}
        </Button>
      </div>

      <div className="text-[11px] text-slate-500 flex items-start gap-1 pt-1">
        <CalendarCheck size={12} className="mt-0.5 text-emerald-600 shrink-0" />
        <span>
          Each request lands on the employee's Requests tab as <b>Pending</b>. Approve/deny from the PTO tab
          just like any other request.
        </span>
      </div>
    </div>
  );
}
